import type {HalImage} from "@cm/hal"

export class WebGl2ImageCacheSizeLookup<ImageType extends HalImage> {
    getAll() {
        return Array.from(this.widthMap.values()).flatMap((list) => list)
    }

    add(image: ImageType) {
        // Insert into widthMap
        let list = this.widthMap.get(image.width)
        if (!list) {
            list = []
            // Insert into sortedWidths in order
            this.widthMap.set(image.width, list)
            const insertIdx = this.binarySearchLowerBound(this.sortedWidths, image.width)
            this.sortedWidths.splice(insertIdx, 0, image.width)
        }
        const insertIdx = this.binarySearchLowerBound(list, image, (t) => t.height)
        list.splice(insertIdx, 0, image)
    }

    remove(image: ImageType) {
        const list = this.widthMap.get(image.width)
        if (!list) {
            return
        }
        const idx = this.binarySearchLowerBound(list, image, (t) => t.height)
        // Find exact match (could have duplicate dimensions)
        for (let i = idx; i < list.length && list[i].height === image.height; i++) {
            if (list[i] === image) {
                list.splice(i, 1)
                break
            }
        }
        // clean up if list becomes empty
        if (list.length === 0) {
            this.widthMap.delete(image.width)
            const widthIdx = this.binarySearchLowerBound(this.sortedWidths, image.width)
            if (this.sortedWidths[widthIdx] === image.width) {
                this.sortedWidths.splice(widthIdx, 1)
            }
        }
    }

    findBest(reqWidth: number, reqHeight: number, maxMarginScale: number): ImageType | null {
        if (maxMarginScale < 1) {
            throw new Error("maxMarginScale must be >= 1")
        }
        const maxWidth = Math.ceil(reqWidth * maxMarginScale)
        const maxHeight = Math.ceil(reqHeight * maxMarginScale)
        const startIndex = this.binarySearchLowerBound(this.sortedWidths, reqWidth)
        let best: ImageType | null = null
        for (let i = startIndex; i < this.sortedWidths.length; i++) {
            const width = this.sortedWidths[i]
            if (width > maxWidth) {
                break
            }
            const list = this.widthMap.get(width)!
            const heightIndex = this.binarySearchLowerBound(list, {height: reqHeight, width} as ImageType, (t) => t.height)
            for (let j = heightIndex; j < list.length; j++) {
                const tex = list[j]
                if (tex.height > maxHeight) {
                    break
                }
                if (tex.width < reqWidth || tex.height < reqHeight || tex.width > maxWidth || tex.height > maxHeight) {
                    throw new Error("Internal error: found texture that does not match requested size")
                }
                if (!best || tex.width * tex.height < best.width * best.height) {
                    best = tex
                }
            }
        }
        return best
    }

    private binarySearchLowerBound<T>(arr: T[], value: T, keyFn: (x: T) => number = (x) => x as unknown as number): number {
        let low = 0
        let high = arr.length
        while (low < high) {
            const mid = (low + high) >> 1
            if (keyFn(arr[mid]) >= keyFn(value)) {
                high = mid
            } else {
                low = mid + 1
            }
        }
        return low
    }

    private widthMap = new Map<number, ImageType[]>()
    private sortedWidths: number[] = []
}
